import { useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { FormGroup } from "../components/FormGroup";
import { FormLabel } from "../components/FormLabel";
import { InfoBox } from "../components/InfoBox";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { PageContent } from "../components/PageContent";
import { SectionTitle } from "../components/SectionTitle";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { StoreCopyItem } from "@KassaroAB/shared/types/enums/StoreCopyItem";
import { JobStatus } from "@KassaroAB/shared/types/enums/JobStatus";
import { isActiveJobStatus } from "@KassaroAB/shared/utils/jobStatus";
import { storeHitLabel, type StoreSearchHit } from "../hooks/useStoreSearch";
import { useGetUserQuery } from "../query/usersQuery";
import { useStoreSearchKeyQuery } from "../query/storesQuery";
import {
  useGetStoreCopyJobsQuery,
  useRetryStoreCopyJobMutation,
  useStartStoreCopyJobMutation,
} from "../query/storeCopyJobsQuery";
import { StoreSearchPicker } from "./components/StoreSearchPicker";

const MAX_VISIBLE_FAILURES = 20;

const itemGroups: { key: string; items: StoreCopyItem[] }[] = [
  {
    key: "settings",
    items: [StoreCopyItem.STORE_SETTINGS],
  },
  {
    key: "catalog",
    items: [
      StoreCopyItem.UNITS,
      StoreCopyItem.COURSES,
      StoreCopyItem.SUPPLIERS,
      StoreCopyItem.BOOKKEEPING_ACCOUNTS,
      StoreCopyItem.PREP_STATIONS,
      StoreCopyItem.PRODUCT_GROUPS,
      StoreCopyItem.MODIFIER_GROUPS,
      StoreCopyItem.MODIFIERS,
      StoreCopyItem.PRODUCTS,
      StoreCopyItem.OFFERS,
      StoreCopyItem.PRODUCT_PAGES,
      StoreCopyItem.LAYOUTS,
    ],
  },
  {
    key: "devices",
    items: [
      StoreCopyItem.PRINTERS,
      StoreCopyItem.BARCODE_FORMATS,
      StoreCopyItem.FLOOR_PLANS,
      StoreCopyItem.DEVICE_CONFIGS,
    ],
  },
];

const allItems = Object.values(StoreCopyItem);

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x3}px;
  max-width: 720px;
`;

const SectionCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
`;

const Description = styled.p`
  margin: 0;
  font-size: ${fontSizes.regular}px;
  color: ${colors.textMuted};
  line-height: 1.5;
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.x2}px;
`;

const GroupTitle = styled.h4`
  margin: 0;
  font-size: ${fontSizes.regular}px;
  color: ${colors.text};
`;

const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${spacing.x1}px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${spacing.x1}px;
  font-size: ${fontSizes.regular}px;
  color: ${colors.text};
  cursor: pointer;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const LinkButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  color: ${colors.primary};
  font-size: ${fontSizes.small}px;
  cursor: pointer;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.x2}px;
`;

const FailureList = styled.ul`
  margin: 0;
  padding-left: ${spacing.x3}px;
  font-size: ${fontSizes.small}px;
  color: ${colors.text};
`;

export const StoreCopyView = () => {
  const { t } = useTranslation();
  const { data: user } = useGetUserQuery();
  const searchKeyQuery = useStoreSearchKeyQuery(user, true);
  const searchConfig = searchKeyQuery.data ?? null;
  const [sourceStore, setSourceStore] = useState<StoreSearchHit | null>(null);
  const [targetStore, setTargetStore] = useState<StoreSearchHit | null>(null);
  const [selectedItems, setSelectedItems] = useState<StoreCopyItem[]>(allItems);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const { data: jobs } = useGetStoreCopyJobsQuery(targetStore?.id);
  const {
    mutate: startCopy,
    isPending: isStarting,
    error: startError,
  } = useStartStoreCopyJobMutation(targetStore?.id);
  const {
    mutate: retryCopy,
    isPending: isRetrying,
    error: retryError,
  } = useRetryStoreCopyJobMutation(targetStore?.id);

  const latestJob = jobs?.[0];
  const isJobActive = latestJob
    ? isActiveJobStatus(latestJob.status)
    : false;
  const jobError = startError || retryError;
  const canStart =
    !!sourceStore &&
    !!targetStore &&
    sourceStore.id !== targetStore.id &&
    selectedItems.length > 0 &&
    !isJobActive &&
    !isStarting;

  const setItems = (items: StoreCopyItem[], checked: boolean) => {
    setSelectedItems((current) =>
      checked
        ? allItems.filter((item) => current.includes(item) || items.includes(item))
        : current.filter((item) => !items.includes(item)),
    );
  };

  return (
    <PageContent title={t("storeCopy.pageTitle")}>
      <Column>
        <SectionCard>
          <SectionTitle>{t("storeCopy.storesTitle")}</SectionTitle>
          <Description>{t("storeCopy.description")}</Description>
          <FormGroup>
            <FormLabel>{t("storeCopy.sourceStore")}</FormLabel>
            <StoreSearchPicker
              searchConfig={searchConfig}
              value={sourceStore}
              onChange={setSourceStore}
              disabledStoreId={targetStore?.id}
              disabled={isJobActive}
            />
          </FormGroup>
          <FormGroup>
            <FormLabel>{t("storeCopy.targetStore")}</FormLabel>
            <StoreSearchPicker
              searchConfig={searchConfig}
              value={targetStore}
              onChange={setTargetStore}
              disabledStoreId={sourceStore?.id}
              disabled={isJobActive}
            />
          </FormGroup>
        </SectionCard>

        <SectionCard>
          <GroupHeader>
            <SectionTitle>{t("storeCopy.itemsTitle")}</SectionTitle>
            <Actions>
              <LinkButton type="button" onClick={() => setItems(allItems, true)}>
                {t("storeCopy.selectAll")}
              </LinkButton>
              <LinkButton type="button" onClick={() => setItems(allItems, false)}>
                {t("storeCopy.selectNone")}
              </LinkButton>
            </Actions>
          </GroupHeader>
          <Description>{t("storeCopy.itemsDescription")}</Description>
          {itemGroups.map((group) => {
            const allChecked = group.items.every((item) =>
              selectedItems.includes(item),
            );
            return (
              <FormGroup key={group.key}>
                <GroupHeader>
                  <GroupTitle>{t(`storeCopy.groups.${group.key}`)}</GroupTitle>
                  {group.items.length > 1 ? (
                    <LinkButton
                      type="button"
                      onClick={() => setItems(group.items, !allChecked)}
                    >
                      {allChecked
                        ? t("storeCopy.selectNone")
                        : t("storeCopy.selectAll")}
                    </LinkButton>
                  ) : null}
                </GroupHeader>
                <CheckboxGrid>
                  {group.items.map((item) => (
                    <CheckboxLabel key={item}>
                      <Checkbox
                        type="checkbox"
                        checked={selectedItems.includes(item)}
                        onChange={(e) => setItems([item], e.target.checked)}
                      />
                      {t(`storeCopy.items.${item}`)}
                    </CheckboxLabel>
                  ))}
                </CheckboxGrid>
              </FormGroup>
            );
          })}
          <Actions>
            <Button
              type="button"
              disabled={!canStart}
              onClick={() => setIsConfirmOpen(true)}
            >
              {isStarting ? t("storeCopy.starting") : t("storeCopy.start")}
            </Button>
            {isJobActive || isStarting ? <LoadingSpinner size={16} /> : null}
          </Actions>
          {jobError ? <InfoBox variant="error">{jobError.message}</InfoBox> : null}
        </SectionCard>

        {latestJob ? (
          <SectionCard>
            <SectionTitle>{t("storeCopy.latestJobTitle")}</SectionTitle>
            {isJobActive ? (
              <InfoBox variant="info">
                {latestJob.currentItem
                  ? t(`storeCopy.items.${latestJob.currentItem}`)
                  : t("storeCopy.status.PROCESSING")}{" "}
                {t("storeCopy.jobProgress", {
                  copied: latestJob.copied,
                  processed: latestJob.processed,
                })}
              </InfoBox>
            ) : null}
            {latestJob.status === JobStatus.COMPLETED ? (
              <InfoBox variant={latestJob.failed > 0 ? "warning" : "success"}>
                {t("storeCopy.jobCompleted", {
                  copied: latestJob.copied,
                  failed: latestJob.failed,
                })}
              </InfoBox>
            ) : null}
            {latestJob.status === JobStatus.FAILED ? (
              <>
                <InfoBox variant="error">
                  {latestJob.error || t("storeCopy.jobFailed")}
                </InfoBox>
                <Actions>
                  <Button
                    type="button"
                    disabled={isRetrying}
                    onClick={() => retryCopy(latestJob.id)}
                  >
                    {isRetrying ? t("storeCopy.retrying") : t("storeCopy.retry")}
                  </Button>
                </Actions>
              </>
            ) : null}
            {latestJob.failures.length > 0 ? (
              <FailureList>
                {latestJob.failures
                  .slice(0, MAX_VISIBLE_FAILURES)
                  .map((failure) => (
                    <li key={`${failure.item}-${failure.sourceId}`}>
                      {t(`storeCopy.items.${failure.item}`)} · {failure.sourceId}:{" "}
                      {failure.reason}
                    </li>
                  ))}
              </FailureList>
            ) : null}
          </SectionCard>
        ) : null}
      </Column>
      <ConfirmDialog
        isOpen={isConfirmOpen}
        title={t("storeCopy.confirmTitle")}
        message={t("storeCopy.confirmMessage", {
          source: sourceStore ? storeHitLabel(sourceStore) : "",
          target: targetStore ? storeHitLabel(targetStore) : "",
        })}
        confirmLabel={t("storeCopy.confirmStart")}
        cancelLabel={t("common.cancel")}
        onCancel={() => setIsConfirmOpen(false)}
        onConfirm={() => {
          setIsConfirmOpen(false);
          if (!sourceStore) return;
          startCopy({ sourceStoreId: sourceStore.id, items: selectedItems });
        }}
      />
    </PageContent>
  );
};
